import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {FaIcon} from "../../FaIcon";
import {
    rankItems,
    splitForHighlight,
    getDidYouMean,
    getChannelVisual,
    stripChannelPrefix,
    SUGGESTED_IDS,
    CATEGORY_ORDER,
} from "./metric_search";


// Labels amistosos por categoría del Data Node. Cualquier categoría no listada
// cae al fallback titleCase (BUSINESS_UNIT_INDICATORS_BUDGET → Business Unit Indicators Budget).
const CATEGORY_LABELS = {
    'BUSINESS_UNIT': 'Configuración',
    'BUSINESS_UNIT_INDICATORS_BUDGET': 'Presupuesto',
    'BUSINESS_UNIT_INDICATORS_GOALS': 'Goals',
    'PERFORMANCE': 'Performance',
    'GOOGLE': 'Google Ads',
    'META': 'Meta Ads',
    'TIKTOK': 'TikTok Ads',
    'LINKEDIN': 'LinkedIn Ads',
    'DV360': 'DV360',
    'GA4': 'Google Analytics 4',
    'GSC': 'Search Console',
    'ECOMMERCE': 'Ecommerce',
    'KLAVIYO': 'Klaviyo',
    '__CUSTOM__': 'Mis métricas',
};

function friendlyCategoryLabel(groupKey) {
    if (CATEGORY_LABELS[groupKey]) return CATEGORY_LABELS[groupKey];
    return groupKey.toLowerCase().split('_').map(w => w ? w[0].toUpperCase() + w.slice(1) : '').join(' ');
}

const CATEGORY_ORDER_INDEX = (() => {
    const map = {};
    CATEGORY_ORDER.forEach((k, i) => { map[k] = i; });
    return map;
})();

// Operadores básicos del MVP. Si más adelante sumamos ROUND/MIN/MAX (D-UI-4),
// se agregan a este array — el resto del componente no cambia.
const OPERATOR_BUTTONS = [
    {symbol: '+', text: ' + ',  ariaLabel: 'Suma'},
    {symbol: '−', text: ' - ',  ariaLabel: 'Resta'},
    {symbol: '×', text: ' * ',  ariaLabel: 'Multiplicación'},
    {symbol: '÷', text: ' / ',  ariaLabel: 'División'},
    {symbol: '(', text: '(',    ariaLabel: 'Paréntesis abierto'},
    {symbol: ')', text: ')',    ariaLabel: 'Paréntesis cerrado'},
];

// Filtros primarios (4 pills compactos). 'Por canal' abre un row secundario con chips de canal.
const FILTER_MODES = {
    ALL: 'ALL',
    IN_USE: 'IN_USE',
    MY_METRICS: 'MY_METRICS',
    BY_CHANNEL: 'BY_CHANNEL',
};


// Extrae identifiers referenciados en la fórmula serializada. Match laxo: cualquier
// secuencia [a-zA-Z_][a-zA-Z0-9_]* — cubre tanto identifiers del Data Node como nombres
// de custom metrics. Over-match no daña (solo "ilumina" como en uso lo que ya está).
function parseInUseIdentifiers(formula) {
    if (!formula) return new Set();
    const matches = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
    return new Set(matches);
}


class FormulaToolboxSidebarComponent extends Component {

    constructor(props) {
        super(props);
        this.state = {
            search: '',
            filterMode: FILTER_MODES.ALL,
            activeChannel: null,   // group key cuando filterMode === BY_CHANNEL
        };
        this._searchRef = React.createRef();
        this._firstRowRef = React.createRef();
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleSearchKeyDown = this.handleSearchKeyDown.bind(this);
        this.handleResultsKeyDown = this.handleResultsKeyDown.bind(this);
    }

    handleSearchChange(e) {
        this.setState({search: e.target.value});
    }

    handleSearchKeyDown(e) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (this._firstRowRef.current) this._firstRowRef.current.focus();
        } else if (e.key === 'Escape' && this.state.search) {
            e.preventDefault();
            this.setState({search: ''});
        }
    }

    handleResultsKeyDown(e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const container = e.currentTarget;
        const buttons = Array.from(container.querySelectorAll('button.formula-toolbox-metric-row'));
        const idx = buttons.indexOf(document.activeElement);
        if (idx < 0) return;
        e.preventDefault();
        if (e.key === 'ArrowDown') {
            const next = buttons[idx + 1];
            if (next) next.focus();
        } else {
            const prev = buttons[idx - 1];
            if (prev) prev.focus();
            else if (this._searchRef.current) this._searchRef.current.focus();
        }
    }

    getAllItems() {
        // Devuelve la lista combinada built-ins + customs en el shape que usa el resto del componente.
        // `cleanDisplayName` es el label visible en la UI (sin prefijo de canal redundante);
        // `displayName` sigue siendo el original para search/ranking y para el token de la fórmula.
        const {metricCatalog, customMetrics} = this.props;

        const builtIns = (metricCatalog || []).map((m) => {
            const group = m.group || 'Otros';
            const displayName = m.display_name || m.id;
            return {
                id: m.id,
                displayName,
                cleanDisplayName: stripChannelPrefix(displayName, group),
                group,
                groupLabel: friendlyCategoryLabel(group),
                visual: getChannelVisual(group),
                isCustom: false,
                isGoalsEvaluable: m.is_goals_evaluable !== false,
                isBroken: false,
            };
        });

        const customs = (customMetrics || []).map((cm) => {
            const displayName = cm.display_name || cm.name;
            return {
                id: cm.name,
                displayName,
                cleanDisplayName: displayName,
                group: '__CUSTOM__',
                groupLabel: 'Mis métricas',
                visual: getChannelVisual('__CUSTOM__'),
                isCustom: true,
                isGoalsEvaluable: true,
                isBroken: !!cm.has_broken_reference,
            };
        });

        return builtIns.concat(customs);
    }

    // Cuenta items por categoría. Lo usamos para el row secundario de "Por canal"
    // y para los counts inline de los pills primarios.
    countByGroup(all) {
        const counts = {};
        all.forEach((m) => {
            counts[m.group] = (counts[m.group] || 0) + 1;
        });
        return counts;
    }

    getInUseIds() {
        return parseInUseIdentifiers(this.props.formula);
    }

    getFilteredItems(all) {
        const {search, filterMode, activeChannel} = this.state;

        let list = all;
        if (filterMode === FILTER_MODES.IN_USE) {
            const inUse = this.getInUseIds();
            list = list.filter((m) => inUse.has(m.id));
        } else if (filterMode === FILTER_MODES.MY_METRICS) {
            list = list.filter((m) => m.isCustom);
        } else if (filterMode === FILTER_MODES.BY_CHANNEL && activeChannel) {
            list = list.filter((m) => m.group === activeChannel);
        }
        return rankItems({items: list, query: search, suggestedIds: SUGGESTED_IDS});
    }

    renderHighlightedName(label) {
        const {search} = this.state;
        const parts = splitForHighlight(label, search);
        if (!parts) return label;
        return (
            <>
                {parts.before}
                <span className="formula-toolbox-highlight">{parts.match}</span>
                {parts.after}
            </>
        );
    }

    renderChannelIcon(visual) {
        if (!visual) return <span className="formula-toolbox-metric-icon -empty" aria-hidden="true"/>;
        return (
            <span className="formula-toolbox-metric-icon" style={{color: visual.color}} aria-hidden="true">
                <FaIcon prefix={visual.prefix} iconName={visual.iconName}/>
            </span>
        );
    }

    renderHeader(t, totalCount) {
        return (
            <div className="formula-toolbox-header">
                <span className="formula-toolbox-title">{t('formula_toolbox.library', 'Biblioteca')}</span>
                <span className="formula-toolbox-total">
                    {t('formula_toolbox.available', '{{count}} disponibles', {count: totalCount})}
                </span>
            </div>
        );
    }

    renderSearch(t) {
        const {search} = this.state;
        return (
            <div className="formula-toolbox-search">
                <FaIcon iconName="magnifying-glass"/>
                <input
                    ref={this._searchRef}
                    type="text"
                    className="form-control"
                    placeholder={t('formula_toolbox.search_placeholder_v2', 'Buscar — costo, cpa, alcance…')}
                    value={search}
                    onChange={this.handleSearchChange}
                    onKeyDown={this.handleSearchKeyDown}/>
            </div>
        );
    }

    renderOperators(t) {
        // Operadores ARRIBA (después del search), no abajo. Botones grandes alineados en una fila.
        return (
            <div className="formula-toolbox-operators" role="group" aria-label={t('formula_toolbox.operators', 'Operadores')}>
                {OPERATOR_BUTTONS.map((op) => (
                    <button
                        key={op.symbol}
                        type="button"
                        className="formula-toolbox-operator-btn"
                        aria-label={op.ariaLabel}
                        onClick={() => this.props.onInsertToken({type: 'operator', text: op.text})}>
                        {op.symbol}
                    </button>
                ))}
            </div>
        );
    }

    renderPrimaryFilters(t, all, byGroup) {
        const {filterMode} = this.state;
        const allCount = all.length;
        const inUseCount = (() => {
            const inUse = this.getInUseIds();
            return all.filter((m) => inUse.has(m.id)).length;
        })();
        const myCount = (byGroup['__CUSTOM__'] || 0);

        const setMode = (mode) => {
            // Toggle: si ya está activo "Por canal", click colapsa volviendo a ALL.
            if (mode === FILTER_MODES.BY_CHANNEL && filterMode === FILTER_MODES.BY_CHANNEL) {
                this.setState({filterMode: FILTER_MODES.ALL, activeChannel: null});
                return;
            }
            this.setState({filterMode: mode, activeChannel: mode === FILTER_MODES.BY_CHANNEL ? null : null});
        };

        const pill = (mode, label, count) => (
            <button
                type="button"
                className={'formula-toolbox-pill' + (filterMode === mode ? ' -active' : '')}
                onClick={() => setMode(mode)}>
                {label}
                {count !== undefined && <span className="formula-toolbox-pill-count">{count}</span>}
            </button>
        );

        return (
            <div className="formula-toolbox-pills">
                {pill(FILTER_MODES.ALL, t('formula_toolbox.all', 'Todas'), allCount)}
                {pill(FILTER_MODES.IN_USE, t('formula_toolbox.in_use', 'En uso'), inUseCount)}
                {pill(FILTER_MODES.MY_METRICS, t('formula_toolbox.my_metrics', 'Mis métricas'), myCount)}
                {pill(FILTER_MODES.BY_CHANNEL, t('formula_toolbox.by_channel', 'Por canal'))}
            </div>
        );
    }

    renderChannelChips(byGroup) {
        // Row secundario que aparece solo cuando filterMode === BY_CHANNEL.
        // Lista canales con count > 0, en orden curado (CATEGORY_ORDER).
        const {activeChannel} = this.state;
        const channelGroups = Object.keys(byGroup)
            .filter((g) => CATEGORY_ORDER_INDEX[g] !== undefined && g !== 'PERFORMANCE' && g !== 'BUSINESS_UNIT_INDICATORS_GOALS'
                          && g !== 'BUSINESS_UNIT_INDICATORS_BUDGET' && g !== 'BUSINESS_UNIT' && g !== '__CUSTOM__')
            .sort((a, b) => CATEGORY_ORDER_INDEX[a] - CATEGORY_ORDER_INDEX[b]);

        if (channelGroups.length === 0) return null;

        return (
            <div className="formula-toolbox-channel-chips">
                {channelGroups.map((g) => {
                    const visual = getChannelVisual(g);
                    return (
                        <button
                            key={g}
                            type="button"
                            className={'formula-toolbox-channel-chip' + (activeChannel === g ? ' -active' : '')}
                            onClick={() => this.setState({activeChannel: activeChannel === g ? null : g})}>
                            {visual && (
                                <span className="formula-toolbox-channel-chip-icon" style={{color: visual.color}}>
                                    <FaIcon prefix={visual.prefix} iconName={visual.iconName}/>
                                </span>
                            )}
                            {friendlyCategoryLabel(g)}
                            <span className="formula-toolbox-channel-chip-count">{byGroup[g]}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    renderEmptyState(t) {
        const {search} = this.state;
        const suggestion = getDidYouMean(search);
        return (
            <div className="formula-toolbox-empty">
                {t('formula_toolbox.no_results', 'Sin resultados.')}
                {suggestion && (
                    <div className="formula-toolbox-empty-suggestion">
                        {t('formula_toolbox.did_you_mean', '¿Quisiste decir')}{' '}
                        <button type="button" onClick={() => this.setState({search: suggestion.queryToApply})}>
                            {suggestion.label}
                        </button>?
                    </div>
                )}
            </div>
        );
    }

    renderMetricRow(m, idx, inUse, refToFirst) {
        const label = m.cleanDisplayName || m.displayName;
        const isInUse = inUse.has(m.id);
        const className = 'formula-toolbox-metric-row'
            + (m.isBroken ? ' -broken' : '')
            + (isInUse ? ' -in-use' : '');
        return (
            <button
                key={(m.isCustom ? 'cm-' : 'bi-') + m.id}
                type="button"
                ref={refToFirst ? this._firstRowRef : null}
                className={className}
                title={m.displayName + (m.isBroken ? ' (referencia rota)' : '') + (isInUse ? ' (en la fórmula actual)' : '')}
                onClick={() => this.props.onInsertToken({
                    type: 'metric', identifier: m.id, displayName: m.displayName,
                    isCustom: m.isCustom, isBroken: m.isBroken,
                })}>
                {this.renderChannelIcon(m.visual)}
                <div className="formula-toolbox-metric-content">
                    <div className="formula-toolbox-metric-row-main">
                        <span className="formula-toolbox-metric-name">{this.renderHighlightedName(label)}</span>
                        {m.isBroken && <FaIcon iconName="triangle-exclamation"/>}
                    </div>
                    <span className="formula-toolbox-metric-channel">{m.groupLabel}</span>
                </div>
                {isInUse && <span className="formula-toolbox-in-use-marker" aria-label="En la fórmula"><FaIcon iconName="check"/></span>}
            </button>
        );
    }

    // Cuando NO hay search, agrupamos por categoría con headers (estilo mockup).
    renderResultsGrouped(items, inUse) {
        const byGroup = {};
        items.forEach((it) => {
            if (!byGroup[it.group]) byGroup[it.group] = [];
            byGroup[it.group].push(it);
        });
        const sortedGroupKeys = Object.keys(byGroup).sort((a, b) => {
            const ra = CATEGORY_ORDER_INDEX[a];
            const rb = CATEGORY_ORDER_INDEX[b];
            if (ra !== undefined && rb !== undefined) return ra - rb;
            if (ra !== undefined) return -1;
            if (rb !== undefined) return 1;
            return a.localeCompare(b, 'es', {sensitivity: 'base'});
        });

        let globalIdx = 0;
        return sortedGroupKeys.map((g) => (
            <div key={g} className="formula-toolbox-group">
                <div className="formula-toolbox-group-head">
                    <span className="formula-toolbox-group-label">{friendlyCategoryLabel(g)}</span>
                    <span className="formula-toolbox-group-count">{byGroup[g].length}</span>
                </div>
                {byGroup[g].map((m) => {
                    const isFirst = globalIdx === 0;
                    globalIdx += 1;
                    return this.renderMetricRow(m, globalIdx, inUse, isFirst);
                })}
            </div>
        ));
    }

    // Cuando HAY search, lista flat ordenada por score (sin headers).
    renderResultsFlat(items, inUse) {
        return items.map((m, idx) => this.renderMetricRow(m, idx, inUse, idx === 0));
    }

    renderResults(t, items) {
        if (items.length === 0) return this.renderEmptyState(t);
        const inUse = this.getInUseIds();
        const grouped = !this.state.search;
        return (
            <div className="formula-toolbox-results" onKeyDown={this.handleResultsKeyDown}>
                {grouped ? this.renderResultsGrouped(items, inUse) : this.renderResultsFlat(items, inUse)}
            </div>
        );
    }

    render() {
        const {t} = this.props;
        const all = this.getAllItems();
        const byGroup = this.countByGroup(all);
        const items = this.getFilteredItems(all);

        return (
            <div className="formula-toolbox-sidebar">
                {this.renderHeader(t, all.length)}
                {this.renderSearch(t)}
                {this.renderOperators(t)}
                {this.renderPrimaryFilters(t, all, byGroup)}
                {this.state.filterMode === FILTER_MODES.BY_CHANNEL && this.renderChannelChips(byGroup)}
                {this.renderResults(t, items)}
            </div>
        );
    }
}

export const FormulaToolboxSidebar = withTranslation()(FormulaToolboxSidebarComponent);
export default FormulaToolboxSidebar;
